/**
 * AdminAIView - AI Memory Management Console (Admin Only)
 *
 * Hidden admin dashboard for managing Marta's 4-layer AI architecture:
 * - Layer 1: Base Instructions (universal behavior)
 * - Layer 2: Sector Templates (business-type specific)
 * - Layer 3: Collective Patterns (crowdsourced knowledge)
 * - Layer 4: Personal Preferences (per-user)
 *
 * This view is NOT visible to regular users - they just see "Marta".
 * Only admin users (role: admin) can access this section.
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppShell } from '../layouts/AppShell';
import { useAuth } from '../contexts/AuthContext';
import {
  memoryService,
  fetchBaseInstructions,
  saveBaseInstructionApi,
  fetchSectors,
  fetchSectorInstructions,
  fetchPatterns,
  fetchMemoryStats,
  type AISector,
  type AICollectivePattern,
  type AIBaseInstruction,
  type AISectorInstruction,
  type AdminDashboardStats,
} from '@business-assistant/shared/ai/memory';

// Tab types
type AdminAITab = 'dashboard' | 'layers' | 'sectors' | 'patterns' | 'analytics';
const VALID_TABS: AdminAITab[] = ['dashboard', 'layers', 'sectors', 'patterns', 'analytics'];

export function AdminAIView() {
  const { setDrawerSectionsOverride } = useAppShell();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { section } = useParams<{ section?: string }>();

  // State
  const [stats, setStats] = useState<AdminDashboardStats | null>(null);
  const [sectors, setSectors] = useState<AISector[]>([]);
  const [pendingPatterns, setPendingPatterns] = useState<AICollectivePattern[]>([]);
  const [allPatterns, setAllPatterns] = useState<AICollectivePattern[]>([]);
  const [baseInstructions, setBaseInstructions] = useState<AIBaseInstruction[]>([]);
  const [sectorInstructions, setSectorInstructions] = useState<AISectorInstruction[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  // Get active tab from URL
  const activeTab: AdminAITab = VALID_TABS.includes(section as AdminAITab)
    ? (section as AdminAITab)
    : 'dashboard';

  // Redirect to dashboard if on bare /admin-ai
  useEffect(() => {
    if (!section || !VALID_TABS.includes(section as AdminAITab)) {
      navigate('/admin-ai/dashboard', { replace: true });
    }
  }, [section, navigate]);

  // Check admin access
  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/home', { replace: true });
    }
  }, [user, navigate]);

  // Load data
  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        // Load from API (all async)
        const [statsData, sectorsData, allPatternsData, pendingData, baseData, sectorInstData] = await Promise.all([
          fetchMemoryStats().catch(() => null),
          fetchSectors().catch(() => []),
          fetchPatterns().catch(() => []),
          fetchPatterns('pending').catch(() => []),
          fetchBaseInstructions().catch(() => []),
          fetchSectorInstructions().catch(() => []),
        ]);

        setStats(statsData ? {
          totalPatterns: statsData.totalPatterns ?? 0,
          pendingPatterns: statsData.pendingPatterns ?? 0,
          approvedPatterns: statsData.approvedPatterns ?? 0,
          rejectedPatterns: statsData.rejectedPatterns ?? 0,
          autoApprovedPatterns: statsData.autoApprovedPatterns ?? 0,
          totalSectors: statsData.totalSectors ?? 0,
          activeSectors: statsData.activeSectors ?? 0,
          totalCorrections: statsData.totalCorrections ?? 0,
          totalUsers: 0,
          usersWithSector: 0,
          usersWithPreferences: 0,
          sectorsWithPatterns: 0,
          processedCorrections: 0,
          avgConfidenceScore: 0,
          patternsLast24h: 0,
          correctionsLast24h: 0,
          preferencesLast24h: 0,
        } : null);
        setSectors(sectorsData);
        setAllPatterns(allPatternsData);
        setPendingPatterns(pendingData);
        setBaseInstructions(baseData);
        setSectorInstructions(sectorInstData);
      } catch (err) {
        console.error('Failed to load admin data:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [refreshKey]);

  // Refresh function
  const refreshData = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  // Tab change handler
  const handleTabChange = useCallback((tab: AdminAITab) => {
    navigate(`/admin-ai/${tab}`);
  }, [navigate]);

  // Setup sidebar
  useEffect(() => {
    const mainItems = [
      {
        id: 'dashboard',
        label: 'Dashboard',
        hint: 'Overview & Stats',
        icon: 'layout-dashboard',
        active: activeTab === 'dashboard',
        onClick: () => handleTabChange('dashboard'),
      },
      {
        id: 'layers',
        label: 'Memory Layers',
        hint: 'Base & Sector Instructions',
        icon: 'layers',
        active: activeTab === 'layers',
        onClick: () => handleTabChange('layers'),
      },
      {
        id: 'sectors',
        label: 'Sectors',
        hint: 'Business Categories',
        icon: 'briefcase',
        active: activeTab === 'sectors',
        onClick: () => handleTabChange('sectors'),
      },
      {
        id: 'patterns',
        label: 'Patterns',
        hint: `Review Queue (${pendingPatterns.length})`,
        icon: 'git-branch',
        active: activeTab === 'patterns',
        onClick: () => handleTabChange('patterns'),
        badge: pendingPatterns.length > 0 ? pendingPatterns.length : undefined,
      },
      {
        id: 'analytics',
        label: 'Analytics',
        hint: 'Learning Metrics',
        icon: 'chart-bar',
        active: activeTab === 'analytics',
        onClick: () => handleTabChange('analytics'),
      },
    ];

    setDrawerSectionsOverride([
      { title: 'AI Admin', items: mainItems },
    ]);
  }, [activeTab, setDrawerSectionsOverride, handleTabChange, pendingPatterns.length]);

  // Don't render if not admin
  if (!user || user.role !== 'admin') {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">Access denied. Admin only.</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-6 bg-gray-50 dark:bg-gray-900">
      {activeTab === 'dashboard' && (
        <DashboardTab stats={stats} loading={loading} pendingCount={pendingPatterns.length} />
      )}
      {activeTab === 'layers' && (
        <LayersTab
          baseInstructions={baseInstructions}
          sectorInstructions={sectorInstructions}
          sectors={sectors}
          onRefresh={refreshData}
        />
      )}
      {activeTab === 'sectors' && (
        <SectorsTab sectors={sectors} loading={loading} />
      )}
      {activeTab === 'patterns' && (
        <PatternsTab
          patterns={pendingPatterns}
          allPatterns={allPatterns}
          loading={loading}
          onRefresh={refreshData}
          userId={user?.id || ''}
        />
      )}
      {activeTab === 'analytics' && (
        <AnalyticsTab />
      )}
    </div>
  );
}

// =============================================================================
// DASHBOARD TAB
// =============================================================================

interface DashboardTabProps {
  stats: AdminDashboardStats | null;
  loading: boolean;
  pendingCount: number;
}

function DashboardTab({ stats, loading, pendingCount }: DashboardTabProps) {
  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Marta AI - Admin Console
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Manage AI memory layers, sector templates, and collective learning
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Sectors"
          value={stats?.totalSectors || 0}
          icon="briefcase"
          color="blue"
        />
        <StatCard
          title="Active Patterns"
          value={stats?.approvedPatterns || 0}
          icon="git-branch"
          color="green"
        />
        <StatCard
          title="Pending Review"
          value={pendingCount}
          icon="clock"
          color="yellow"
          highlight={pendingCount > 0}
        />
        <StatCard
          title="Total Corrections"
          value={stats?.totalCorrections || 0}
          icon="edit"
          color="purple"
        />
      </div>

      {/* Quick Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
        <div className="flex flex-wrap gap-3">
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
            + Add Base Instruction
          </button>
          <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition">
            + Add Sector
          </button>
          {pendingCount > 0 && (
            <button className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition">
              Review {pendingCount} Pending Patterns
            </button>
          )}
        </div>
      </div>

      {/* Architecture Explanation */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">4-Layer Architecture</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <LayerCard
            number={1}
            title="Base Instructions"
            description="Universal behavior rules that apply to all users. You control Marta's core personality here."
            color="blue"
          />
          <LayerCard
            number={2}
            title="Sector Templates"
            description="Industry-specific knowledge (electricians, lawyers, etc.). Automatic when user selects sector."
            color="green"
          />
          <LayerCard
            number={3}
            title="Collective Patterns"
            description="Crowdsourced knowledge from similar users. Auto-detected and reviewed by you."
            color="yellow"
          />
          <LayerCard
            number={4}
            title="Personal Preferences"
            description="Per-user customizations learned over time. Individual, private, user-controlled."
            color="purple"
          />
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// LAYERS TAB
// =============================================================================

interface LayersTabProps {
  baseInstructions: AIBaseInstruction[];
  sectorInstructions: AISectorInstruction[];
  sectors: AISector[];
  onRefresh: () => void;
}

function LayersTab({ baseInstructions, sectorInstructions, sectors, onRefresh }: LayersTabProps) {
  const [selectedSectorId, setSelectedSectorId] = useState<string>('');
  const [editingInstruction, setEditingInstruction] = useState<AIBaseInstruction | null>(null);

  const filteredSectorInstructions = selectedSectorId
    ? sectorInstructions.filter(i => i.sectorId === selectedSectorId)
    : [];

  const handleToggleInstruction = async (instruction: AIBaseInstruction) => {
    await saveBaseInstructionApi({
      ...instruction,
      isActive: !instruction.isActive,
    });
    onRefresh();
  };

  const categoryColors: Record<string, string> = {
    personality: 'bg-blue-100 text-blue-700',
    capabilities: 'bg-purple-100 text-purple-700',
    restrictions: 'bg-red-100 text-red-700',
    formatting: 'bg-green-100 text-green-700',
    privacy: 'bg-yellow-100 text-yellow-700',
    behavior: 'bg-gray-100 text-gray-700',
    terminology: 'bg-cyan-100 text-cyan-700',
    regulations: 'bg-orange-100 text-orange-700',
    pricing: 'bg-emerald-100 text-emerald-700',
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Memory Layers
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Manage base instructions and sector templates
        </p>
      </div>

      {/* Layer 1: Base Instructions */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Layer 1: Base Instructions</h2>
            <p className="text-sm text-gray-500">Universal rules for all users ({baseInstructions.length} total)</p>
          </div>
          <button
            onClick={() => setEditingInstruction({
              id: '',
              name: '',
              instructionText: '',
              category: 'behavior',
              priority: 100,
              isActive: true,
              createdAt: '',
              updatedAt: '',
            })}
            className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
          >
            + Add Instruction
          </button>
        </div>
        <div className="p-4 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-sm text-gray-500">
                <th className="pb-2">Name</th>
                <th className="pb-2">Category</th>
                <th className="pb-2">Priority</th>
                <th className="pb-2">Status</th>
                <th className="pb-2">Actions</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {baseInstructions
                .sort((a, b) => b.priority - a.priority)
                .map(instruction => (
                  <tr key={instruction.id} className="border-t border-gray-100 dark:border-gray-700">
                    <td className="py-3">
                      <span className="font-medium">{instruction.name}</span>
                      <p className="text-xs text-gray-500 truncate max-w-xs">
                        {instruction.instructionText.substring(0, 60)}...
                      </p>
                    </td>
                    <td className="py-3">
                      <span className={`px-2 py-0.5 rounded text-xs ${categoryColors[instruction.category] || 'bg-gray-100 text-gray-700'}`}>
                        {instruction.category}
                      </span>
                    </td>
                    <td className="py-3">{instruction.priority}</td>
                    <td className="py-3">
                      <span className={`px-2 py-0.5 rounded text-xs ${instruction.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {instruction.isActive ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td className="py-3">
                      <button
                        onClick={() => setEditingInstruction(instruction)}
                        className="text-blue-600 hover:underline mr-2"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleToggleInstruction(instruction)}
                        className={instruction.isActive ? 'text-gray-400 hover:text-red-600' : 'text-green-600 hover:text-green-700'}
                      >
                        {instruction.isActive ? 'Disable' : 'Enable'}
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Layer 2: Sector Instructions */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold">Layer 2: Sector Instructions</h2>
          <p className="text-sm text-gray-500">Select a sector to view/edit instructions</p>
        </div>
        <div className="p-4">
          <select
            value={selectedSectorId}
            onChange={(e) => setSelectedSectorId(e.target.value)}
            className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600"
          >
            <option value="">-- Select Sector --</option>
            {sectors.map(sector => (
              <option key={sector.id} value={sector.id}>
                {sector.parentSectorId ? '  └ ' : ''}{sector.nameIt} ({sector.name})
              </option>
            ))}
          </select>

          {selectedSectorId ? (
            filteredSectorInstructions.length > 0 ? (
              <div className="mt-4 space-y-3">
                {filteredSectorInstructions.map(instruction => (
                  <div key={instruction.id} className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{instruction.name}</span>
                      <span className={`px-2 py-0.5 rounded text-xs ${categoryColors[instruction.category] || 'bg-gray-100 text-gray-700'}`}>
                        {instruction.category}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {instruction.instructionText}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-4 text-gray-500 text-center py-4">
                No instructions for this sector yet.
              </p>
            )
          ) : (
            <p className="mt-4 text-gray-500 text-center py-8">
              Select a sector to view its instructions
            </p>
          )}
        </div>
      </div>

      {/* Edit Instruction Modal */}
      {editingInstruction && (
        <InstructionEditModal
          instruction={editingInstruction}
          onSave={async (updated) => {
            await saveBaseInstructionApi(updated);
            setEditingInstruction(null);
            onRefresh();
          }}
          onClose={() => setEditingInstruction(null)}
        />
      )}
    </div>
  );
}

// Instruction Edit Modal
function InstructionEditModal({
  instruction,
  onSave,
  onClose,
}: {
  instruction: AIBaseInstruction;
  onSave: (instruction: AIBaseInstruction) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState(instruction);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-auto">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold">
            {instruction.id ? 'Edit Instruction' : 'New Instruction'}
          </h2>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600"
              placeholder="Instruction name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Category</label>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value as any })}
              className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600"
            >
              <option value="personality">Personality</option>
              <option value="capabilities">Capabilities</option>
              <option value="restrictions">Restrictions</option>
              <option value="formatting">Formatting</option>
              <option value="privacy">Privacy</option>
              <option value="behavior">Behavior</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Priority (higher = more important)</label>
            <input
              type="number"
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) || 100 })}
              className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Instruction Text</label>
            <textarea
              value={form.instructionText}
              onChange={(e) => setForm({ ...form, instructionText: e.target.value })}
              className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600 min-h-32"
              placeholder="Enter the instruction text that Marta will follow..."
            />
          </div>
        </div>
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 border rounded hover:bg-gray-100">
            Cancel
          </button>
          <button
            onClick={() => onSave(form)}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// SECTORS TAB
// =============================================================================

interface SectorsTabProps {
  sectors: AISector[];
  loading: boolean;
}

function SectorsTab({ sectors, loading }: SectorsTabProps) {
  if (loading) {
    return <LoadingSpinner />;
  }

  // Organize sectors hierarchically
  const parentSectors = sectors.filter(s => !s.parentSectorId);
  const childSectorMap = sectors.reduce((acc, s) => {
    if (s.parentSectorId) {
      if (!acc[s.parentSectorId]) acc[s.parentSectorId] = [];
      acc[s.parentSectorId].push(s);
    }
    return acc;
  }, {} as Record<string, AISector[]>);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Business Sectors
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Industry categories for AI customization
          </p>
        </div>
        <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
          + Add Sector
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {parentSectors.map(sector => (
          <div key={sector.id} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                <span className="text-blue-600 dark:text-blue-400">{sector.icon || '📁'}</span>
              </div>
              <div>
                <h3 className="font-semibold">{sector.nameIt}</h3>
                <p className="text-xs text-gray-500">{sector.name}</p>
              </div>
            </div>

            {childSectorMap[sector.id] && childSectorMap[sector.id].length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-500 mb-2">Sub-sectors:</p>
                <div className="flex flex-wrap gap-2">
                  {childSectorMap[sector.id].map(child => (
                    <span
                      key={child.id}
                      className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs"
                    >
                      {child.nameIt}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-3 flex gap-2">
              <button className="text-sm text-blue-600 hover:underline">Edit</button>
              <button className="text-sm text-gray-400 hover:text-green-600">+ Add Sub</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// PATTERNS TAB
// =============================================================================

interface PatternsTabProps {
  patterns: AICollectivePattern[];
  allPatterns: AICollectivePattern[];
  loading: boolean;
  onRefresh: () => void;
  userId: string;
}

function PatternsTab({ patterns, allPatterns, loading, onRefresh, userId }: PatternsTabProps) {
  const [selectedPattern, setSelectedPattern] = useState<AICollectivePattern | null>(null);
  const [showAll, setShowAll] = useState(false);

  const displayPatterns = showAll ? allPatterns : patterns;

  const handleApprove = async (patternId: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    await memoryService.reviewPattern({ patternId, action: 'approve' }, userId);
    onRefresh();
    setSelectedPattern(null);
  };

  const handleReject = async (patternId: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    await memoryService.reviewPattern({ patternId, action: 'reject' }, userId);
    onRefresh();
    setSelectedPattern(null);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Pattern Review Queue
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Review and approve patterns detected from user interactions
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowAll(!showAll)}
            className={`px-3 py-1.5 text-sm rounded ${showAll ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            {showAll ? 'Show Pending Only' : 'Show All Patterns'}
          </button>
          <button
            onClick={onRefresh}
            className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
          >
            Refresh
          </button>
        </div>
      </div>

      {displayPatterns.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
          <div className="text-4xl mb-4">🎉</div>
          <h2 className="text-xl font-semibold mb-2">
            {showAll ? 'No patterns yet' : 'All caught up!'}
          </h2>
          <p className="text-gray-500">
            {showAll
              ? 'Start chatting with Marta to generate patterns.'
              : 'No patterns pending review. As users interact with Marta, new patterns will appear here.'}
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-500">
              {displayPatterns.length} patterns {showAll ? 'total' : 'awaiting review'}
            </p>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {displayPatterns.map(pattern => (
              <div
                key={pattern.id}
                className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                onClick={() => setSelectedPattern(pattern)}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                        {pattern.patternType}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        pattern.approvalStatus === 'approved' ? 'bg-green-100 text-green-700' :
                        pattern.approvalStatus === 'auto_approved' ? 'bg-emerald-100 text-emerald-700' :
                        pattern.approvalStatus === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {pattern.approvalStatus}
                      </span>
                      <span className="text-sm font-medium">{pattern.patternKey}</span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {pattern.patternValue}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span>Occurrences: {pattern.occurrenceCount}</span>
                      <span>Confidence: {pattern.confidenceScore}%</span>
                    </div>
                  </div>
                  {pattern.approvalStatus === 'pending' && (
                    <div className="flex gap-2">
                      <button
                        className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                        onClick={(e) => handleApprove(pattern.id, e)}
                      >
                        Approve
                      </button>
                      <button
                        className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                        onClick={(e) => handleReject(pattern.id, e)}
                      >
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pattern Detail Modal */}
      {selectedPattern && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-auto">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Pattern Details</h2>
              <button
                onClick={() => setSelectedPattern(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="text-sm text-gray-500">Type</label>
                <p>{selectedPattern.patternType}</p>
              </div>
              <div>
                <label className="text-sm text-gray-500">Key</label>
                <p className="font-medium">{selectedPattern.patternKey}</p>
              </div>
              <div>
                <label className="text-sm text-gray-500">Value</label>
                <p className="bg-gray-100 dark:bg-gray-700 p-3 rounded">
                  {selectedPattern.patternValue}
                </p>
              </div>
              {selectedPattern.contextDescription && (
                <div>
                  <label className="text-sm text-gray-500">Context</label>
                  <p className="text-sm text-gray-600">{selectedPattern.contextDescription}</p>
                </div>
              )}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-gray-500">Occurrences</label>
                  <p className="text-lg font-semibold">{selectedPattern.occurrenceCount}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Confidence</label>
                  <p className="text-lg font-semibold">{selectedPattern.confidenceScore}%</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Status</label>
                  <p className="text-lg font-semibold capitalize">{selectedPattern.approvalStatus}</p>
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex gap-3 justify-end">
              <button
                onClick={() => setSelectedPattern(null)}
                className="px-4 py-2 border rounded hover:bg-gray-100"
              >
                Close
              </button>
              {selectedPattern.approvalStatus === 'pending' && (
                <>
                  <button
                    onClick={() => handleReject(selectedPattern.id)}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => handleApprove(selectedPattern.id)}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Approve
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// ANALYTICS TAB
// =============================================================================

function AnalyticsTab() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Learning Analytics
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Track AI learning progress and pattern effectiveness
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Coming Soon</h2>
        <p className="text-gray-500">
          Analytics dashboard with charts showing:
        </p>
        <ul className="list-disc list-inside mt-4 text-gray-600 space-y-2">
          <li>Patterns detected over time</li>
          <li>Approval rate trends</li>
          <li>Sector-wise learning progress</li>
          <li>User correction frequency</li>
          <li>Average confidence scores</li>
          <li>Pattern effectiveness (success rate)</li>
        </ul>
      </div>
    </div>
  );
}

// =============================================================================
// SHARED COMPONENTS
// =============================================================================

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent" />
    </div>
  );
}

interface StatCardProps {
  title: string;
  value: number;
  icon: string;
  color: 'blue' | 'green' | 'yellow' | 'purple';
  highlight?: boolean;
}

function StatCard({ title, value, color, highlight }: StatCardProps) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    purple: 'bg-purple-100 text-purple-600',
  };

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow p-4 ${highlight ? 'ring-2 ring-yellow-400' : ''}`}>
      <div className={`w-10 h-10 ${colorClasses[color]} rounded-lg flex items-center justify-center mb-3`}>
        <span className="text-lg">📊</span>
      </div>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-sm text-gray-500">{title}</p>
    </div>
  );
}

interface LayerCardProps {
  number: number;
  title: string;
  description: string;
  color: 'blue' | 'green' | 'yellow' | 'purple';
}

function LayerCard({ number, title, description, color }: LayerCardProps) {
  const colorClasses = {
    blue: 'border-blue-500 bg-blue-50 dark:bg-blue-900/20',
    green: 'border-green-500 bg-green-50 dark:bg-green-900/20',
    yellow: 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20',
    purple: 'border-purple-500 bg-purple-50 dark:bg-purple-900/20',
  };

  return (
    <div className={`border-l-4 ${colorClasses[color]} rounded-r-lg p-4`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-lg font-bold text-gray-400">L{number}</span>
        <span className="font-semibold">{title}</span>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400">{description}</p>
    </div>
  );
}

export default AdminAIView;
